import api, {
  buildCompanyScopeHeaders,
} from "./api";

export const DEFAULT_SUPPLIER_CONTRACT_LIMIT = 50;

function normalizeNonNegativeInteger(
  value,
  fallback,
) {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isInteger(parsed) || parsed < 0) {
    return fallback;
  }

  return parsed;
}

function normalizePositiveInteger(
  value,
  fallback,
) {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
}

function normalizeOptionalString(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}

function normalizeOptionalBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  return null;
}

function requireNonEmptyString(
  value,
  fieldName,
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    throw new TypeError(
      `${fieldName} must be a non-empty string.`,
    );
  }

  return value.trim();
}

function buildSupplierContractsBasePath(
  supplierUuid,
) {
  const normalizedSupplierUuid = requireNonEmptyString(
    supplierUuid,
    "supplierUuid",
  );

  return `/supply-chain/suppliers/${encodeURIComponent(
    normalizedSupplierUuid,
  )}/contracts`;
}

export function buildSupplierContractListParams({
  offset = 0,
  limit = DEFAULT_SUPPLIER_CONTRACT_LIMIT,
  search = "",
  active = null,
  validAt = "",
} = {}) {
  const params = {
    offset: normalizeNonNegativeInteger(
      offset,
      0,
    ),
    limit: normalizePositiveInteger(
      limit,
      DEFAULT_SUPPLIER_CONTRACT_LIMIT,
    ),
  };

  const normalizedSearch = normalizeOptionalString(
    search,
  );

  if (normalizedSearch) {
    params.search = normalizedSearch;
  }

  const normalizedActive = normalizeOptionalBoolean(
    active,
  );

  if (normalizedActive !== null) {
    params.active = normalizedActive;
  }

  const normalizedValidAt = normalizeOptionalString(
    validAt,
  );

  if (normalizedValidAt) {
    params.valid_at = normalizedValidAt;
  }

  return params;
}

export function getSupplierContractErrorMessage(
  error,
  fallback = "Unable to process the supplier contract request.",
) {
  const responseData = error?.response?.data;

  if (
    typeof responseData?.message === "string"
    && responseData.message.trim()
  ) {
    return responseData.message.trim();
  }

  if (
    typeof responseData?.error?.message === "string"
    && responseData.error.message.trim()
  ) {
    return responseData.error.message.trim();
  }

  if (
    typeof responseData?.error === "string"
    && responseData.error.trim()
  ) {
    return responseData.error.trim();
  }

  if (
    typeof error?.message === "string"
    && error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
}

export async function listSupplierContracts(
  supplierUuid,
  {
    companyUuid,
    offset = 0,
    limit = DEFAULT_SUPPLIER_CONTRACT_LIMIT,
    search = "",
    active = null,
    validAt = "",
  } = {},
) {
  const response = await api.get(
    buildSupplierContractsBasePath(supplierUuid),
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
      params: buildSupplierContractListParams({
        offset,
        limit,
        search,
        active,
        validAt,
      }),
    },
  );

  return response.data;
}
